import React, { useState } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    navn: '',
    telefon: '',
    email: '',
    beskrivelse: '',
    website_url: '' // Honeypot
  });

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.navn || !formData.telefon || !formData.email || !formData.beskrivelse) {
      setStatus({ submitting: false, success: false, error: 'Venligst udfyld alle påkrævede felter.' });
      return;
    }

    setStatus({ submitting: true, success: false, error: null });

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setStatus({ submitting: false, success: true, error: null });
        setFormData({
          navn: '',
          telefon: '',
          email: '',
          beskrivelse: '',
          website_url: ''
        });
      } else {
        const errData = await response.json().catch(() => ({}));
        setStatus({
          submitting: false,
          success: false,
          error: errData.message || 'Der opstod en fejl under afsendelsen. Prøv venligst igen.'
        });
      }
    } catch (err) {
      setStatus({
        submitting: false,
        success: false,
        error: 'Netværksfejl. Kontroller venligst din internetforbindelse og prøv igen.'
      });
    }
  };

  return (
    <div className="bg-white p-8 rounded-none border-b border-slate-900 shadow-sm">
      <h3 className="font-display font-bold text-2xl text-slate-900 mb-6">
        Send os en besked
      </h3>

      {status.success ? (
        <div className="bg-emerald-50 border border-emerald-100 text-emerald-800 p-6 rounded-xl mb-6 flex items-start gap-3">
          <i className="fa-solid fa-circle-check text-xl text-emerald-500 mt-0.5" />
          <div>
            <h4 className="font-semibold mb-1">Tak for din henvendelse!</h4>
            <p className="text-sm">Vi har modtaget din besked og vender tilbage til dig hurtigst muligt.</p>
          </div>
        </div>
      ) : null}

      {status.error ? (
        <div className="bg-rose-50 border border-rose-100 text-rose-800 p-4 rounded-xl mb-6 flex items-start gap-3">
          <i className="fa-solid fa-circle-exclamation text-xl text-rose-500 mt-0.5" />
          <p className="text-sm">{status.error}</p>
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Honeypot field - absolutely hidden from humans */}
        <div className="sr-only absolute -top-[9999px] -left-[9999px]" aria-hidden="true">
          <label htmlFor="website_url">Do not fill this field</label>
          <input
            id="website_url"
            type="text"
            name="website_url"
            value={formData.website_url}
            onChange={handleChange}
            tabIndex="-1"
            autoComplete="off"
          />
        </div>

        <div>
          <label htmlFor="navn" className="block text-sm font-medium text-slate-700 mb-1">
            Navn <span className="text-rose-500">*</span>
          </label>
          <input
            id="navn"
            type="text"
            name="navn"
            required
            value={formData.navn}
            onChange={handleChange}
            className="w-full px-1 py-2.5 rounded-none border-b border-slate-200 focus:outline-none focus:border-brand-primary transition-all bg-transparent"
            placeholder="Dit fulde navn"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label htmlFor="telefon" className="block text-sm font-medium text-slate-700 mb-1">
              Telefonnummer <span className="text-rose-500">*</span>
            </label>
            <input
              id="telefon"
              type="tel"
              name="telefon"
              required
              value={formData.telefon}
              onChange={handleChange}
              className="w-full px-1 py-2.5 rounded-none border-b border-slate-200 focus:outline-none focus:border-brand-primary transition-all bg-transparent"
              placeholder="F.eks. 22 43 91 47"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
              E-mail <span className="text-rose-500">*</span>
            </label>
            <input
              id="email"
              type="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="w-full px-1 py-2.5 rounded-none border-b border-slate-200 focus:outline-none focus:border-brand-primary transition-all bg-transparent"
              placeholder="din@email.dk"
            />
          </div>
        </div>

        <div>
          <label htmlFor="beskrivelse" className="block text-sm font-medium text-slate-700 mb-1">
            Beskrivelse af opgaven <span className="text-rose-500">*</span>
          </label>
          <textarea
            id="beskrivelse"
            name="beskrivelse"
            required
            rows="5"
            value={formData.beskrivelse}
            onChange={handleChange}
            className="w-full px-1 py-2.5 rounded-none border-b border-slate-200 focus:outline-none focus:border-brand-primary transition-all resize-none bg-transparent"
            placeholder="Beskriv dit projekt, f.eks. sommerhusrenovering, nyt tag, udskiftning af vinduer og døre..."
          />
        </div>

        <button
          type="submit"
          disabled={status.submitting}
          className="w-full bg-brand-primary hover:bg-brand-primary/90 text-white font-semibold py-3.5 px-6 rounded-none transition-all shadow-sm flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
        >
          {status.submitting ? (
            <>
              <i className="fa-solid fa-spinner animate-spin" />
              Sender besked...
            </>
          ) : (
            <>
              <i className="fa-solid fa-paper-plane" />
              Send besked
            </>
          )}
        </button>
      </form>
    </div>
  );
}
